import React, { useCallback } from 'react';
import { withTranslation } from 'react-i18next';

import { Box, IconButton, Typography } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FollowMeIcon from '@core/dashboard/components/EventDrawer/EventDrawerContent/ParticipantsTab/ParticipantItem/FollowMeIcon';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useChatState } from '@core/chat';
import { useConference } from '../../../../DashboardContent/Dolby';
import { useRedirectToPrivateChat } from '@core/chat/StreamChat/hooks';
import { useMytaverse } from '@providers/MytaverseProvider';
import UserAvatar from '@components/UserAvatar';

import { sendCustomSentryBreadcrumb } from '@helpers/sentry';

import EventsService from '@services/EventsService';
import ParticipantService from '../../../../DashboardContent/Dolby/ParticipantService';

import { ParticipantItemProps } from './interfaces';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';

import { useStyles } from './styles';

import { ChatIcon } from '@core/chat/icons';
import ButtonTooltip from '@core/dashboard/components/ButtonTooltip';
import ParticipantStatusIcon from '@components/ParticipantStatusIcon';
import { HandPalmIcon } from '@icons/HandPalm';
import UserTag from '@components/UserTag';
import ParticipantScopes from '@constants/scopes';

const ParticipantItem = ({
  participant,
  currentParticipantId,
  onClose,
  t: translate,
}: ParticipantItemProps) => {
  const classes = useStyles();
  const { setPreviewingParticipant } = useConference();
  const {
    currentEvent,
    currentParticipant,
    teleportToParticipant,
    pendingFollowersData,
    acceptedFollowersData,
    isTeleporting,
  } = useMytaverseEvent();
  const { sendMessageToEvent, sendMessageToUnreal, userPermissions } =
    useMytaverse();
  const { setOpen: setOpenChat } = useChatState();

  const isCurrentParticipant =
    participant.participantId === currentParticipantId;

  const isCurrentTeleport = userPermissions?.includes(
    ParticipantScopes.HubFollowMe,
  );
  const isParticipantOnline = !!participant?.roomId;

  const handleParticipantPreview = useCallback(async () => {
    const participantProfile = await EventsService.getParticipantProfile(
      participant.participantId,
    );
    setPreviewingParticipant(participantProfile);
  }, [participant, setPreviewingParticipant]);

  const redirectToPrivateChat = useRedirectToPrivateChat();

  const handleChatIconClick = useCallback(async () => {
    sendCustomSentryBreadcrumb({
      message: `got to private chat with userId=${participant.participantId} from event drawer`,
    });
    onClose();
    await redirectToPrivateChat(participant.participantId);
    setOpenChat(true);
  }, [onClose, participant, redirectToPrivateChat, setOpenChat]);

  const handleTeleportToParticipant = useCallback(async () => {
    onClose();
    await teleportToParticipant(participant);
  }, [teleportToParticipant, participant, onClose]);

  const sendFollowMeMessageToEvent = (action: WebsocketAction) => {
    if (!currentEvent?.eventId) return;
    if (action === WebsocketAction.InviteFollowingByAdmin) {
      sendMessageToUnreal({
        action,
        followerId: participant.participantId,
        ownerId: currentParticipantId,
        ownerName: currentParticipant?.firstName,
        ownerGameSessionId: currentParticipant?.gameSessionId,
        ownerRoomId: currentParticipant?.roomId,
      });
    }

    sendMessageToEvent(currentEvent.eventId, {
      action,
      followerId: participant.participantId,
      ownerId: currentParticipantId,
      ownerName: currentParticipant?.firstName,
      ownerGameSessionId: currentParticipant?.gameSessionId,
      ownerRoomId: currentParticipant?.roomId,
    });
  };

  const isFacilitator = participant?.isFacilitator || false;

  return (
    <Box sx={classes.root} component="div">
      <Box sx={classes.rootPosition} component="div">
        <Box sx={classes.userAvatar}>
          <UserAvatar
            src={participant.avatarImage}
            sx={classes.avatar}
            name={participant.name || participant.email}
            initials={ParticipantService.getDisplayInitials(participant)}
          />
          {isParticipantOnline && (
            <ParticipantStatusIcon
              isOnline={isParticipantOnline}
              sx={classes.statusIcon}
            />
          )}
        </Box>
        <Box sx={classes.usernameWrapper(isFacilitator)}>
          <Typography sx={classes.username}>
            {participant.name || participant.email}
          </Typography>
          {isFacilitator && <UserTag />}
        </Box>
      </Box>

      <Box>
        {!isCurrentParticipant && isParticipantOnline ? (
          <Box sx={classes.tools} className="helpers">
            {isCurrentTeleport && (
              <FollowMeIcon
                participant={participant}
                pendingFollowersData={pendingFollowersData}
                acceptedFollowersData={acceptedFollowersData}
                currentParticipantId={currentParticipantId}
                sendFollowMeMessageToEvent={sendFollowMeMessageToEvent}
                sx={classes.iconButton}
              />
            )}
            {participant.handRaised && (
              <ButtonTooltip title={translate('participantsModal.handRaised')}>
                <IconButton sx={classes.iconButton}>
                  <HandPalmIcon sx={{ ...classes.icon, color: '#FFCC00' }} />
                </IconButton>
              </ButtonTooltip>
            )}
            <ButtonTooltip title={translate('participantsModal.chat')}>
              <IconButton sx={classes.iconButton} onClick={handleChatIconClick}>
                <ChatIcon sx={classes.icon} />
              </IconButton>
            </ButtonTooltip>
            <IconButton
              sx={classes.iconButtonTeleport}
              disabled={isTeleporting}
              onClick={handleTeleportToParticipant}
            >
              <LocationOnIcon />
            </IconButton>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default withTranslation()(ParticipantItem);
